<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch, nextTick } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useUI } from '@/composables/useUI'
import { reservarZIndex, liberarZIndex } from '@/composables/useZIndex'
import { formatarDataHoraBR } from '@/utils/useDataBR'
import { ESTQ_RESERVA_CABECALHO_ST } from '@/constants/ESTQ_RESERVA_CABECALHO_ST'
import { ESTQ_RESERVA_ORIGEM } from '@/constants/ESTQ_RESERVA_ORIGEM'
import { criarConfigBuscaProduto } from '@/search-definitions/produtoSearch'
import { filialSearch } from '@/search-definitions/filialSearch'
import { criarConfigBuscaLocalEstoque } from '@/search-definitions/localEstoqueSearch'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'

const emit = defineEmits(['show-notification'])
const authStore = useAuthStore()
const { obterClasseStatusGhost } = useUI()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)

// ─── Estado ─────────────────────────────────────────────────
const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('desc')

const filtros = reactive({
  idProduto: '',
  nomeProduto: '',
  idFilial: '',
  nomeFilial: '',
  idLocalEstoque: '',
  nomeLocalEstoque: '',
  lstStatusReserva: '',
  lstTipoDocumentoOrigem: '',
  buscaLivre: ''
})

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

// ─── Modal de confirmação de cancelamento (z-index manual) ─
const mostrarModalCancelar = ref(false)
const cancelarItem = ref(null)
const cancelando = ref(false)
const tokenZIndexCancelar = ref(null)
const zIndexCancelar = ref(null)

// ─── Modal Nova Reserva (ModalFormularioV2 gerencia z-index) ─
const mostrarModalNovaReserva = ref(false)
const novaReservaForm = reactive({
  idProduto: '',
  nomeProduto: '',
  idFilial: '',
  nomeFilial: '',
  idLocalEstoque: '',
  nomeLocalEstoque: '',
  quantidadeUnBasica: '',
  lstUnidadeMedidaBasica: '',
  quantidadeVolumes: '',
  lstUnidadeMedidaVolume: ''
})
const salvandoReserva = ref(false)
const errors = ref({})

// ─── Exportação ────────────────────────────────────────────
const exportandoProgresso = ref({ total: 0, carregados: 0 })
const dadosExportar = ref(null)

// ─── RBAC ──────────────────────────────────────────────────
const temPermissaoCompleta = computed(() => authStore.pode('RESERVAS_ESTOQUE_COMPLETO'))
const temPermissaoVisualizacao = computed(() => authStore.pode('RESERVAS_ESTOQUE_VISUALIZACAO'))
const podeVerAcoes = computed(() => temPermissaoCompleta.value || temPermissaoVisualizacao.value)

// ─── Configurações de busca ────────────────────────────────
const configBuscaProduto = computed(() => ({
  ...criarConfigBuscaProduto(),
  titulo: 'Buscar Produto',
  filtrosFixos: { idEmpresa: authStore.empresaAtiva }
}))

const configBuscaFilial = computed(() => filialSearch)

const configBuscaLocalEstoque = computed(() => {
  if (!novaReservaForm.idFilial) return null
  return criarConfigBuscaLocalEstoque(
    authStore.organizacaoAtiva,
    Number(novaReservaForm.idFilial)
  )
})

// ─── Descrição textual dos filtros (para exportação) ───────
const descricaoFiltros = computed(() => {
  const partes = []
  if (filtros.buscaLivre) partes.push(`Busca: "${filtros.buscaLivre}"`)
  if (filtros.idProduto) partes.push(`Produto ID: ${filtros.idProduto}`)
  if (filtros.idFilial) partes.push(`Filial ID: ${filtros.idFilial}`)
  if (filtros.idLocalEstoque) partes.push(`Local ID: ${filtros.idLocalEstoque}`)
  if (filtros.lstStatusReserva) partes.push(`Status: ${filtros.lstStatusReserva}`)
  if (filtros.lstTipoDocumentoOrigem) partes.push(`Origem: ${filtros.lstTipoDocumentoOrigem}`)
  return partes.length ? partes.join(' | ') : 'Nenhum filtro aplicado'
})

// ─── Formatação ────────────────────────────────────────────
function formatarDecimal (valor) {
  if (valor == null || valor === '') return '-'
  const num = Number(valor)
  if (Number.isNaN(num)) return '-'
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })
}

// ─── Colunas ───────────────────────────────────────────────
const colunasPadrao = [
  { field: 'idReservaEstoque', label: 'ID', width: 80, sortable: false },
  { field: 'skuProduto', label: 'SKU', width: 100, sortable: false, formatter: (v) => v || '-' },
  { field: 'nomeProduto', label: 'Produto', sortable: false, class: 'fw-medium text-body-emphasis', formatter: (v) => v || '-' },
  { field: 'nomeFilial', label: 'Filial', width: 140, sortable: false },
  { field: 'nomeLocalEstoque', label: 'Local', width: 160, sortable: false },
  { field: 'lstStatusItemReserva', label: 'Status', width: 100, align: 'center', sortable: true, slotName: 'status' },
  { field: 'lstStatusReserva', label: 'Status Cabeçalho', width: 120, align: 'center', sortable: true, visible: false },
  { field: 'lstTipoDocumentoOrigem', label: 'Origem', width: 120, sortable: true, slotName: 'origem' },
  { field: 'descricaoOrigem', label: 'Documento Origem', width: 120, sortable: false, formatter: (v) => { if (!v) return '-'; const m = String(v).match(/#(\d+)/); return m ? '#' + m[1] : v } },
  { field: 'quantidadeReservadaUnBasica', label: 'Qtd Reservada', width: 110, align: 'right', sortable: false, formatter: formatarDecimal },
  { field: 'tsCriacao', label: 'Criada em', width: 140, align: 'center', sortable: true, formatter: (v) => v ? formatarDataHoraBR(new Date(v)) : '-' },
  // ── Âncoras (opcionais, visíveis no seletor de colunas) ──
  { field: 'idDocumentoEntrada', label: 'ID Doc. Entrada', width: 100, sortable: false, visible: false },
  { field: 'numeroDocumentoEntrada', label: 'Nº Doc. Entrada', width: 140, sortable: false, visible: false },
  { field: 'nomeProdutor', label: 'Produtor', width: 160, sortable: false, visible: false },
  { field: 'numeroProcessoEmbarque', label: 'Nº Embarque', width: 140, sortable: false, visible: false },
  { field: 'numeroContainer', label: 'Container', width: 140, sortable: false, visible: false },
  { field: 'idEmbarquePackingListProduto', label: 'PLP', width: 90, sortable: false, visible: false },
  { field: 'idLoteFabricacao', label: 'Lote', width: 90, sortable: false, visible: false }
]

const campoSlotMap = {
  idReservaEstoque: 'id',
  lstStatusItemReserva: 'status',
  lstTipoDocumentoOrigem: 'origem',
  acoes: 'acoes'
}

const colunas = computed(() => {
  const base = colunasPadrao.map(c => ({
    ...c,
    slotName: campoSlotMap[c.field] || undefined
  }))
  if (podeVerAcoes.value) {
    return [
      ...base,
      { field: 'acoes', label: 'Ações', width: 100, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
    ]
  }
  return base
})

// ─── Sort Resolver (client-side para campos Lst*) ──────────
const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null
  return [...data].sort((a, b) => {
    const valA = a[field + 'Texto'] || a[field] || ''
    const valB = b[field + 'Texto'] || b[field] || ''
    const cmp = String(valA).localeCompare(String(valB), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => { buscarDados(1) }

// ─── Watch: Troca de Empresa ───────────────────────────────
watch(() => authStore.empresaAtiva, (n) => {
  if (n) {
    buscarDados(1)
  } else {
    lista.value = []; paginacao.value.TotalCount = 0
  }
})

// ─── Busca de Dados ────────────────────────────────────────
async function buscarDados (pageNumber = 1) {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    lista.value = []
    return
  }
  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)
    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (filtros.idProduto) params.append('idProduto', filtros.idProduto)
    if (filtros.idFilial) params.append('idFilial', filtros.idFilial)
    if (filtros.idLocalEstoque) params.append('idLocalEstoque', filtros.idLocalEstoque)
    if (filtros.lstStatusReserva) params.append('lstStatusReserva', filtros.lstStatusReserva)
    if (filtros.lstTipoDocumentoOrigem) params.append('lstTipoDocumentoOrigem', filtros.lstTipoDocumentoOrigem)
    if (filtros.buscaLivre) params.append('buscaLivre', filtros.buscaLivre)
    if (sortField.value) params.append('sortBy', sortField.value)
    if (sortOrder.value) params.append('isDescending', sortOrder.value === 'desc')

    const response = await api.get(`/ReservasEstoque?${params.toString()}`)
    lista.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    erro.value = 'Não foi possível carregar os dados.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally { loading.value = false }
}

const aplicarFiltros = () => { buscarDados(1) }
const limparFiltros = () => {
  Object.keys(filtros).forEach(k => { filtros[k] = '' })
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) buscarDados(page)
}
const mudarTamanhoPagina = (n) => { paginacao.value.PageSize = n; buscarDados(1) }

// ─── Exportação ─────────────────────────────────────────────
async function handleExportarDados () {
  if (!authStore.empresaAtiva) {
    emit('show-notification', { message: 'Selecione uma empresa antes de exportar.', type: 'warning' })
    return
  }

  const totalRegistros = paginacao.value.TotalCount ?? 0
  if (!totalRegistros) {
    emit('show-notification', { message: 'Nenhum dado para exportar.', type: 'warning' })
    return
  }

  exportandoProgresso.value = { total: totalRegistros, carregados: 0 }
  dadosExportar.value = null

  const pageSize = paginacao.value.PageSize || 25
  const totalPages = Math.ceil(totalRegistros / pageSize)
  let todosDados = []

  try {
    for (let page = 1; page <= totalPages; page++) {
      const params = new URLSearchParams()
      params.append('pageNumber', page)
      params.append('pageSize', pageSize)

      if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
      if (filtros.idProduto) params.append('idProduto', filtros.idProduto)
      if (filtros.idFilial) params.append('idFilial', filtros.idFilial)
      if (filtros.idLocalEstoque) params.append('idLocalEstoque', filtros.idLocalEstoque)
      if (filtros.lstStatusReserva) params.append('lstStatusReserva', filtros.lstStatusReserva)
      if (filtros.lstTipoDocumentoOrigem) params.append('lstTipoDocumentoOrigem', filtros.lstTipoDocumentoOrigem)
      if (filtros.buscaLivre) params.append('buscaLivre', filtros.buscaLivre)
      if (sortField.value) params.append('sortBy', sortField.value)
      if (sortOrder.value) params.append('isDescending', sortOrder.value === 'desc')

      const response = await api.get(`/ReservasEstoque?${params.toString()}`, { _suppressLoading: true })
      const dados = response.data
      if (Array.isArray(dados)) {
        todosDados = todosDados.concat(dados)
      }

      exportandoProgresso.value = { total: totalRegistros, carregados: todosDados.length }
    }

    dadosExportar.value = [...todosDados]
    await nextTick()
    exportandoProgresso.value = { total: 0, carregados: 0 }
  } catch (e) {
    console.error('[ReservasEstoque] Erro na exportação:', e)
    emit('show-notification', { message: 'Erro ao coletar dados para exportação.', type: 'error' })
    exportandoProgresso.value = { total: 0, carregados: 0 }
  }
}

// ─── Auditoria ──────────────────────────────────────────────
const abrirAuditoria = (item) => {
  auditoriaSelecionada.id = item.idReservaEstoque
  auditoriaSelecionada.nome = `Reserva #${item.idReservaEstoque}`
  mostrarAuditoria.value = true
}

// ─── Cancelamento Individual ────────────────────────────────
const confirmarCancelamento = (item) => {
  cancelarItem.value = item
  mostrarModalCancelar.value = true
  const { token, zIndex } = reservarZIndex()
  tokenZIndexCancelar.value = token
  zIndexCancelar.value = zIndex
}

const executarCancelamento = async () => {
  if (!cancelarItem.value) return
  cancelando.value = true
  try {
    await api.post(`/ReservasEstoque/${cancelarItem.value.idReservaEstoque}/cancelar`)
    emit('show-notification', { message: `Reserva #${cancelarItem.value.idReservaEstoque} cancelada com sucesso.`, type: 'success' })
    mostrarModalCancelar.value = false
    cancelarItem.value = null
    buscarDados(paginacao.value.CurrentPage || 1)
  } catch (error) {
    const data = error.response?.data
    const msg = data?.message || 'Erro ao cancelar reserva.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally { cancelando.value = false }
}

const fecharModalCancelar = () => {
  mostrarModalCancelar.value = false
  if (tokenZIndexCancelar.value) {
    liberarZIndex(tokenZIndexCancelar.value)
    tokenZIndexCancelar.value = null
  }
}

// ─── Nova Reserva Manual ────────────────────────────────────
const abrirNovaReserva = () => {
  Object.keys(novaReservaForm).forEach(k => { novaReservaForm[k] = '' })
  Object.keys(errors.value).forEach(k => delete errors.value[k])
  mostrarModalNovaReserva.value = true
}

const validarNovaReserva = () => {
  const errs = {}
  if (!novaReservaForm.idProduto) errs.idProduto = 'Selecione um produto.'
  if (!novaReservaForm.idFilial) errs.idFilial = 'Informe a filial.'
  if (!novaReservaForm.idLocalEstoque) errs.idLocalEstoque = 'Informe o local de estoque.'
  if (!novaReservaForm.quantidadeUnBasica || Number(novaReservaForm.quantidadeUnBasica) <= 0) errs.quantidadeUnBasica = 'Informe a quantidade.'
  if (!novaReservaForm.lstUnidadeMedidaBasica) errs.lstUnidadeMedidaBasica = 'Informe a unidade de medida.'
  Object.keys(errs).forEach(k => { errors.value[k] = errs[k] })
  Object.keys(errors.value).forEach(k => { if (!errs[k]) delete errors.value[k] })
  return Object.keys(errs).length === 0
}

const salvarNovaReserva = async () => {
  if (!validarNovaReserva()) return
  salvandoReserva.value = true
  try {
    const payload = {
      idProduto: Number(novaReservaForm.idProduto),
      idFilial: Number(novaReservaForm.idFilial),
      idLocalEstoque: Number(novaReservaForm.idLocalEstoque),
      quantidadeUnBasica: Number(novaReservaForm.quantidadeUnBasica),
      lstUnidadeMedidaBasica: novaReservaForm.lstUnidadeMedidaBasica || undefined,
      quantidadeVolumes: novaReservaForm.quantidadeVolumes ? Number(novaReservaForm.quantidadeVolumes) : undefined,
      lstUnidadeMedidaVolume: novaReservaForm.lstUnidadeMedidaVolume || undefined
    }
    await api.post('/ReservasEstoque', payload)
    emit('show-notification', { message: 'Reserva criada com sucesso.', type: 'success' })
    mostrarModalNovaReserva.value = false
    buscarDados(1)
  } catch (error) {
    const data = error.response?.data
    const msg = data?.message || 'Erro ao criar reserva.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally { salvandoReserva.value = false }
}

// ─── Ciclo de Vida ─────────────────────────────────────────
onMounted(() => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) buscarDados(1)
})

onActivated(() => { buscarDados(paginacao.value.CurrentPage || 1) })
defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    titulo="Reservas de Estoque"
    subtitulo="Gestão de reservas de estoque por produto, filial e local"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="temPermissaoCompleta"
    label-botao-adicionar="Nova Reserva"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="abrirNovaReserva"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label class="form-label">Busca Livre</label>
          <input type="text" class="form-control" v-model="filtros.buscaLivre" placeholder="Buscar por descrição ou observação..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-4">
          <CampoBusca id="filtroProduto" label="Produto" v-model="filtros.idProduto" :configuracao-busca="configBuscaProduto" @selecionado="val => filtros.nomeProduto = val.nome" />
        </div>
        <div class="col-md-4">
          <CampoBusca id="idFilial" label="Filial" v-model="filtros.idFilial" :configuracao-busca="configBuscaFilial" @selecionado="val => filtros.nomeFilial = val.identificacao" />
        </div>
        <div class="col-md-3">
          <CampoBusca id="idLocalEstoque" label="Local de Estoque" v-model="filtros.idLocalEstoque" :configuracao-busca="criarConfigBuscaLocalEstoque(authStore.organizacaoAtiva, filtros.idFilial)" @selecionado="val => filtros.nomeLocalEstoque = val.descricao" />
        </div>
        <div class="col-md-3">
          <CampoBuscaLista id="lstStatusReserva" label="Status" tabela="ReservaEstoque" v-model="filtros.lstStatusReserva" />
        </div>
        <div class="col-md-3">
          <CampoBuscaLista id="lstTipoDocumentoOrigem" label="Origem" tabela="ReservaEstoque" v-model="filtros.lstTipoDocumentoOrigem" />
        </div>
        <div class="col-md-12 text-end">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhuma reserva de estoque encontrada."
      row-key="idReservaEstoque"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      :resolver-listas-tabela="['ReservaEstoque', 'ReservaEstoqueItem']"
      modulo-layout="ReservasEstoque"
      :colunas-padrao="colunasPadrao"
      :exportando-progresso="exportandoProgresso"
      :dados-exportar="dadosExportar"
      titulo-exportacao="Reservas de Estoque"
      :filtros-exportacao="descricaoFiltros"
      @sort-change="handleSortChange"
      @show-notification="emit('show-notification', $event)"
      @exportar-dados="handleExportarDados"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idReservaEstoque }}</span>
      </template>

      <template #status="{ row }">
        <span class="badge" :class="obterClasseStatusGhost(row.lstStatusItemReservaTexto || row.lstStatusItemReserva)">
          {{ row.lstStatusItemReservaTexto || row.lstStatusItemReserva }}
        </span>
      </template>

      <template #origem="{ row }">
        <span class="small text-body-secondary">{{ row.lstTipoDocumentoOrigemTexto || row.lstTipoDocumentoOrigem }}</span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper" v-if="podeVerAcoes">
          <button class="btn-icon text-secondary" @click="abrirAuditoria(row)" title="Ver Histórico">
            <i class="bi bi-clock-history"></i>
          </button>
          <button
            v-if="temPermissaoCompleta && row.lstTipoDocumentoOrigem === ESTQ_RESERVA_ORIGEM.Manual && row.lstStatusReserva === ESTQ_RESERVA_CABECALHO_ST.Ativa"
            class="btn-icon text-danger"
            @click="confirmarCancelamento(row)"
            title="Cancelar Reserva"
          >
            <i class="bi bi-x-circle"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <!-- Modal de confirmação de cancelamento individual -->
  <div v-if="mostrarModalCancelar" class="modal-backdrop" :style="{ zIndex: zIndexCancelar }" @click="fecharModalCancelar">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content shadow">
        <div class="modal-header">
          <h5 class="modal-title"><i class="bi bi-exclamation-triangle text-danger me-2"></i> Cancelar Reserva</h5>
          <button type="button" class="btn-close" @click="fecharModalCancelar"></button>
        </div>
        <div class="modal-body">
          <p v-if="cancelarItem">
            Deseja cancelar a reserva <strong>#{{ cancelarItem.idReservaEstoque }}</strong>
            do produto <strong>{{ cancelarItem.nomeProduto || '#' + cancelarItem.idProduto }}</strong>?
          </p>
          <p class="text-muted mb-0">O saldo de estoque será liberado.</p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="fecharModalCancelar">Voltar</button>
          <button class="btn btn-danger" :disabled="cancelando" @click="executarCancelamento">
            <i v-if="cancelando" class="bi bi-arrow-repeat me-2"></i>
            {{ cancelando ? 'Cancelando...' : 'Confirmar Cancelamento' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal de nova reserva manual (ModalFormularioV2) -->
  <ModalFormularioV2
    v-model="mostrarModalNovaReserva"
    titulo="Nova Reserva Manual"
    :loading="salvandoReserva"
    tamanho="lg"
    @confirmar="salvarNovaReserva"
  >
    <div v-if="Object.keys(errors).length > 0" class="alert alert-danger py-2 mb-3">
      <i class="bi bi-exclamation-triangle me-2"></i>
      <span v-for="(msg, field) in errors" :key="field" class="d-block small">{{ msg }}</span>
    </div>
    <div class="row g-3">
      <div class="col-md-6">
        <CampoBusca id="novoIdProduto" label="Produto" v-model="novaReservaForm.idProduto" :configuracao-busca="configBuscaProduto" @selecionado="val => novaReservaForm.nomeProduto = val.nome" />
      </div>
      <div class="col-md-6">
        <CampoBusca id="idFilial" label="Filial" v-model="novaReservaForm.idFilial" :configuracao-busca="configBuscaFilial" @selecionado="val => { novaReservaForm.nomeFilial = val.identificacao; novaReservaForm.idLocalEstoque = ''; novaReservaForm.nomeLocalEstoque = '' }" />
      </div>
      <div class="col-md-6">
        <CampoBusca
          id="idLocalEstoque"
          label="Local de Estoque"
          v-model="novaReservaForm.idLocalEstoque"
          :configuracao-busca="configBuscaLocalEstoque"
          :disabled="!novaReservaForm.idFilial"
          @selecionado="val => novaReservaForm.nomeLocalEstoque = val.descricao"
        />
      </div>
      <div class="col-md-6">
        <CampoInput id="quantidadeUnBasica" label="Quantidade (Un. Básica)" v-model="novaReservaForm.quantidadeUnBasica" type="decimal" />
      </div>
      <div class="col-md-6">
        <CampoBuscaLista id="lstUnidadeMedidaBasica" label="Unidade de Medida" tabela="ReservaEstoqueItem" v-model="novaReservaForm.lstUnidadeMedidaBasica" />
      </div>
      <div class="col-md-6">
        <CampoInput id="quantidadeVolumes" label="Volumes (opcional)" v-model="novaReservaForm.quantidadeVolumes" type="decimal" />
      </div>
      <div class="col-md-6">
        <CampoBuscaLista id="lstUnidadeMedidaVolume" label="Unid. Volume (opcional)" tabela="ReservaEstoqueItem" v-model="novaReservaForm.lstUnidadeMedidaVolume" />
      </div>
    </div>
  </ModalFormularioV2>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="ReservaEstoque"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    contexto="tenant"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
</style>
